import React from 'react';

// lodash
import _ from 'lodash';

// components
import {
  Input as MuiInput,
  InputAdornment as MuiInputAdornment,
  styled,
  type InputProps,
} from '@mui/material';

import SearchIcon from '@mui/icons-material/Search';

interface Props extends Omit<InputProps, 'onChange'> {
  readonly delay?: number;
  readonly onChange: (value: string) => void;
}

export const SearchField = ({ delay = 200, onChange, ...props }: Props) => {
  const onChangeRef = React.useRef(onChange);
  onChangeRef.current = onChange;

  /**
   * Memoised on `delay` alone.
   *
   * <p>The debounced function used to be rebuilt on every render, so a keystroke
   * that landed after a re-render scheduled a fresh timer that could not cancel
   * the previous one -- the field debounced within a burst of typing and leaked
   * a call per render. Reading `onChange` through a ref keeps the identity
   * stable without going stale.
   */
  const handleChange = React.useMemo(
    () =>
      _.debounce<(value: string) => void>((value) => {
        if (typeof onChangeRef.current === 'function') {
          onChangeRef.current(value);
        }
      }, delay),
    [delay]
  );

  React.useEffect(() => () => handleChange.cancel(), [handleChange]);

  return (
    <Input
      endAdornment={
        <InputAdornment position="end">
          <SearchIcon />
        </InputAdornment>
      }
      placeholder="Search"
      {...props}
      onChange={(event) => handleChange(event.target.value)}
    />
  );
};

const Input = styled(MuiInput)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  borderRadius: 5,
  width: '100%',

  '& input': {
    border: 'none',
    backgroundColor: 'transparent',

    '&:focus': {
      backgroundColor: 'transparent',
      color: theme.palette.primary.contrastText,
    },
  },
}));

const InputAdornment = styled(MuiInputAdornment)({
  margin: 0,
  paddingRight: 5,
});
